/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

import { usePagination } from "@material-ui/lab/Pagination";
import Link from "next/link";
import styled from "styled-components";
import clsx from "clsx";
import { useWindowResize } from "utils/hooks";
import { PageButtonWrapperPropsType, PaginationPropsType } from "./types";

const PageLink = styled.a`
	text-decoration: none;
	min-width: 30px;
	min-height: 30px;
	display: block;
`;

const PageButtonWrapper: React.FC<PageButtonWrapperPropsType> = ({
	isSSRPagination,
	baseUrl,
	children,
	page,
}) =>
	isSSRPagination ? (
		<Link href={page > 1 ? { pathname: baseUrl, query: { page } } : baseUrl} passHref>
			<PageLink>{children}</PageLink>
		</Link>
	) : (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>{children}</>
	);

const Pagination: React.FC<PaginationPropsType> = ({
	currentPage,
	totalPages,
	handlePageChange,
	baseUrl = "/jobs",
	isSSRPagination,
	mobileSiblingCount = 2,
}) => {
	const [isMobileView] = useWindowResize();

	const { items } = usePagination({
		count: totalPages || 10,
		siblingCount: isMobileView ? mobileSiblingCount : 3,
		page: currentPage || 1,
		onChange: handlePageChange
			? (e, pageNumber) => {
					handlePageChange(e, pageNumber);
			  }
			: null,
	});

	return (
		<div
			className={clsx(` h-[48px] w-[512px]`, {
				"[h-56px] w-[336px]": isMobileView,
			})}
		>
			<ul
				className="m-0 flex list-none items-center justify-center p-0"
				data-testid="pagination"
			>
				{items.map(({ page, type, selected, disabled, ...item }, index) => {
					let children = null;
					if (type === "start-ellipsis") {
						children = (
							<span className="flex cursor-default items-center">...</span>
						);
					} else if (type === "previous") {
						children = (
							<PageButtonWrapper
								isSSRPagination={isSSRPagination}
								page={page}
								baseUrl={baseUrl}
							>
								<button
									className={clsx(
										`border-0 bg-[#F4F2F6] text-left text-[12px] font-[400] leading-[16px]`,
										{
											"cursor-not-allowed text-[#8C8594]": disabled,
										}
									)}
									type="button"
									disabled={disabled}
									{...item}
								>
									Prev
								</button>
							</PageButtonWrapper>
						);
					} else if (type === "next") {
						children = (
							<PageButtonWrapper
								isSSRPagination={isSSRPagination}
								page={page}
								baseUrl={baseUrl}
							>
								<button
									disabled={disabled}
									className={clsx(
										`font-[400]) border-0 bg-[#F4F2F6] text-left text-[12px] leading-[16px]`,
										{
											"cursor-not-allowed text-[#8C8594]": disabled,
										}
									)}
									type="button"
									{...item}
								>
									Next
								</button>
							</PageButtonWrapper>
						);
					} else if (
						page === totalPages &&
						type === "page" &&
						items.findIndex((i) => i.type === "end-ellipsis") !== -1
					) {
						return null;
					} else if (type === "page") {
						children = (
							<PageButtonWrapper
								isSSRPagination={isSSRPagination}
								page={page}
								baseUrl={baseUrl}
							>
								<button
									className={clsx(
										`h-[32px] w-full rounded-[4px] text-[12px] font-[400] leading-[16px]`,
										{
											"h-[24px]": isMobileView,
										}
									)}
									type="button"
									{...item}
								>
									{page}
								</button>
							</PageButtonWrapper>
						);
					} else if (type === "end-ellipsis") {
						return null;
					} else {
						children = (
							<PageButtonWrapper
								isSSRPagination={isSSRPagination}
								page={page}
								baseUrl={baseUrl}
							>
								<button disabled={disabled} type="button" {...item}>
									{type}
								</button>
							</PageButtonWrapper>
						);
					}

					return (
						<li
							className={clsx(
								`m-[4px] flex h-[32px] w-[32px] list-none items-center justify-center rounded-[4px] bg-white`,
								{
									"border-none !bg-[#F4F2F6]":
										type === "previous" || type === "next",
									"!bg-[#F4F2F6]": type === "start-ellipsis",
									"border-[1px] border-solid border-[#1F8268]":
										type === "page" && selected,
									"border-[1px] border-solid border-[#E8E7EA]":
										isMobileView && !selected && type === "page",
									"rounded-[4px] bg-[#EAF8F4] text-[#1F8268]": selected,
								}
							)}
							key={index}
						>
							{children}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default Pagination;
